import { useEffect, useState, FormEvent } from "react";
import { useTheme } from "../../contexts/ThemeContext";

const MAILERLITE_ACTION =
  "https://assets.mailerlite.com/jsonp/2208570/forms/182464527424554064/subscribe";

interface GetStartedModalProps {
  onClose: () => void;
}

export default function GetStartedModal({ onClose }: GetStartedModalProps) {
  const { isDark } = useTheme();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      const body = new URLSearchParams();
      body.append("fields[email]", email.trim());
      body.append("ml-submit", "1");
      body.append("anticsrf", "true");

      await fetch(MAILERLITE_ACTION, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ animation: "fadeIn 0.2s ease" }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm cursor-pointer"
        onClick={onClose}
      />

      {/* Modal Card */}
      <div
        className={`relative z-10 w-full max-w-md rounded-2xl overflow-hidden border transition-colors duration-300 ${
          isDark ? "bg-[#0d0d0d] border-white/10" : "bg-white border-black/8"
        }`}
        style={{ animation: "slideUp 0.25s ease" }}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between px-6 py-4 border-b ${
            isDark ? "border-white/8" : "border-black/6"
          }`}
        >
          <div>
            <p
              className={`font-syne font-semibold text-base ${
                isDark ? "text-white" : "text-[#111111]"
              }`}
            >
              Get Early Access
            </p>
            <p
              className={`font-inter text-xs mt-0.5 ${
                isDark ? "text-[#666666]" : "text-[#888888]"
              }`}
            >
              Join 800+ resellers on the waitlist
            </p>
          </div>
          <button
            onClick={onClose}
            className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors duration-200 cursor-pointer ${
              isDark
                ? "text-[#666666] hover:text-white hover:bg-white/8"
                : "text-[#888888] hover:text-[#111111] hover:bg-black/6"
            }`}
            aria-label="Close"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-8">
          {submitted ? (
            <div className="flex flex-col items-center gap-4 py-6">
              <div
                className={`w-14 h-14 flex items-center justify-center rounded-full ${
                  isDark ? "bg-white/8" : "bg-black/5"
                }`}
              >
                <i
                  className={`ri-check-line text-2xl ${
                    isDark ? "text-white" : "text-[#111111]"
                  }`}
                />
              </div>
              <div className="text-center">
                <p
                  className={`font-syne font-semibold text-base mb-1 ${
                    isDark ? "text-white" : "text-[#111111]"
                  }`}
                >
                  You&apos;re on the list!
                </p>
                <p
                  className={`font-inter text-sm ${
                    isDark ? "text-[#666666]" : "text-[#888888]"
                  }`}
                >
                  Check your email for a welcome message.
                </p>
              </div>
              <button
                onClick={onClose}
                className={`mt-2 font-inter text-sm px-6 py-2.5 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  isDark
                    ? "bg-white text-black hover:bg-[#e8e8e8]"
                    : "bg-[#111111] text-white hover:bg-[#2a2a2a]"
                }`}
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <p
                className={`font-inter text-sm leading-relaxed mb-6 ${
                  isDark ? "text-[#666666]" : "text-[#666666]"
                }`}
              >
                Enter your email below and we&apos;ll send you a welcome message
                as soon as you&apos;re in.
              </p>

              <form
                data-readdy-form
                id="get-started-modal-form"
                action={MAILERLITE_ACTION}
                method="post"
                onSubmit={handleSubmit}
                className="flex flex-col gap-3"
              >
                <input
                  type="email"
                  name="fields[email]"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className={`w-full rounded-full px-5 py-3.5 font-inter text-sm focus:outline-none transition-colors duration-200 border ${
                    isDark
                      ? "bg-white/5 border-white/10 text-white placeholder:text-[#555555] focus:border-white/25"
                      : "bg-black/4 border-black/10 text-[#111111] placeholder:text-[#888888] focus:border-black/25"
                  }`}
                />
                <input type="hidden" name="ml-submit" value="1" />
                <input type="hidden" name="anticsrf" value="true" />

                <button
                  type="submit"
                  disabled={submitting}
                  className={`w-full font-inter font-medium text-sm px-7 py-3.5 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2 justify-center ${
                    isDark
                      ? "bg-white text-black hover:bg-[#e8e8e8]"
                      : "bg-[#111111] text-white hover:bg-[#2a2a2a]"
                  }`}
                >
                  {submitting ? "Joining..." : "Get Early Access"}
                  {!submitting && <i className="ri-arrow-right-line" />}
                </button>
              </form>

              {error && (
                <p
                  className={`font-inter text-xs mt-2 text-center ${
                    isDark ? "text-red-400" : "text-red-500"
                  }`}
                >
                  {error}
                </p>
              )}

              <p
                className={`font-inter text-xs mt-4 text-center ${
                  isDark ? "text-[#555555]" : "text-[#888888]"
                }`}
              >
                Free early access · No credit card required
              </p>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}
